import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, UncontrolledTooltip } from "reactstrap";

/**
 * Threshold status badge component.
 * Shows warning/blocked status for private individual referrers.
 */
interface ThresholdStatusProps {
  thresholdWarning: boolean;
  thresholdBlocked: boolean;
  thresholdBlockReason?: string;
  id?: string;
  size?: "sm" | "md";
}

const ThresholdStatus = ({
  thresholdWarning,
  thresholdBlocked,
  thresholdBlockReason,
  id = "threshold-status",
  size = "md",
}: ThresholdStatusProps) => {
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile.threshold" });

  if (thresholdBlocked) {
    return (
      <>
        <Badge id={id} color="danger" className={size === "sm" ? "badge-sm" : ""} pill>
          <i className="fas fa-ban mr-1" />
          {t("blocked", "Blocked")}
        </Badge>
        <UncontrolledTooltip target={id} placement="top">
          {thresholdBlockReason || t("blockedTooltip", "Payment threshold exceeded")}
        </UncontrolledTooltip>
      </>
    );
  }

  if (thresholdWarning) {
    return (
      <>
        <Badge id={id} color="warning" className={size === "sm" ? "badge-sm" : ""} pill>
          <i className="fas fa-exclamation-triangle mr-1" />
          {t("warning", "Warning")}
        </Badge>
        <UncontrolledTooltip target={id} placement="top">
          {t("warningTooltip", "Approaching payment threshold")}
        </UncontrolledTooltip>
      </>
    );
  }

  return null;
};

/**
 * Compact version for use in tables/lists.
 */
interface ThresholdStatusIconProps {
  thresholdWarning: boolean;
  thresholdBlocked: boolean;
  thresholdBlockReason?: string;
  id?: string;
}

export const ThresholdStatusIcon = ({
  thresholdWarning,
  thresholdBlocked,
  thresholdBlockReason,
  id = "threshold-icon",
}: ThresholdStatusIconProps) => {
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile.threshold" });

  if (thresholdBlocked) {
    return (
      <>
        <i id={id} className="fas fa-ban text-danger" style={{ cursor: "help" }} />
        <UncontrolledTooltip target={id} placement="top">
          {thresholdBlockReason || t("blockedTooltip", "Payment threshold exceeded")}
        </UncontrolledTooltip>
      </>
    );
  }

  if (thresholdWarning) {
    return (
      <>
        <i id={id} className="fas fa-exclamation-triangle text-warning" style={{ cursor: "help" }} />
        <UncontrolledTooltip target={id} placement="top">
          {t("warningTooltip", "Approaching payment threshold")}
        </UncontrolledTooltip>
      </>
    );
  }

  return null;
};

export default ThresholdStatus;
